import React, { useState } from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { withApollo } from 'react-apollo';
import { withToastManager } from 'react-toast-notifications';

import {
  CREATE_PAYMENT_INTENT,
  CURRENT_USER,
  GET_ASYNC_TEMPORARY_RESPONSES,
  UPCOMING_APPOINTMENTS,
  GET_MEMBERSHIPS,
  APPOINTMENTS,
} from '../../../../graphql';
import { Box, Button, Flex, Text } from 'rebass';
import withSession from '../../../../lib/withSession';
import UseCurrentCard from './checkout/UseCurrentCard';
import PleaseWaitRotatingText from '../../../../components/PleaseWaitRotatingText';

const CARD_OPTIONS = {
  iconStyle: 'solid',
  style: {
    base: {
      iconColor: 'rgba(53, 78, 121, 0.5)',
      color: '#354E79',
      fontWeight: 300,
      fontFamily: 'Poppins, Open Sans, Segoe UI, sans-serif',
      fontSize: '14px',
      fontSmoothing: 'antialiased',
      border: '1px solid rgba(0, 0, 0, 0.1);',
      ':-webkit-autofill': {
        color: '#fce883',
      },
      '::placeholder': {
        color: 'rgba(53, 78, 121, 1)',
      },
    },
    invalid: {
      iconColor: 'rgba(255, 15, 0, 1)',
      color: 'rgba(255, 15, 0, 1)',
    },
  },
};

const ErrorMessage = ({ children }) => (
  <Box className="ErrorMessage" role="alert" mt={3}>
    <svg width="16" height="16" viewBox="0 0 17 17">
      <path
        fill="#FFF"
        d="M8.5,17 C3.80557963,17 0,13.1944204 0,8.5 C0,3.80557963 3.80557963,0 8.5,0 C13.1944204,0 17,3.80557963 17,8.5 C17,13.1944204 13.1944204,17 8.5,17 Z"
      />
      <path
        fill="#6772e5"
        d="M8.5,7.29791847 L6.12604076,4.92395924 C5.79409512,4.59201359 5.25590488,4.59201359 4.92395924,4.92395924 C4.59201359,5.25590488 4.59201359,5.79409512 4.92395924,6.12604076 L7.29791847,8.5 L4.92395924,10.8739592 C4.59201359,11.2059049 4.59201359,11.7440951 4.92395924,12.0760408 C5.25590488,12.4079864 5.79409512,12.4079864 6.12604076,12.0760408 L8.5,9.70208153 L10.8739592,12.0760408 C11.2059049,12.4079864 11.7440951,12.4079864 12.0760408,12.0760408 C12.4079864,11.7440951 12.4079864,11.2059049 12.0760408,10.8739592 L9.70208153,8.5 L12.0760408,6.12604076 C12.4079864,5.79409512 12.4079864,5.25590488 12.0760408,4.92395924 C11.7440951,4.59201359 11.2059049,4.59201359 10.8739592,4.92395924 L8.5,7.29791847 L8.5,7.29791847 Z"
      />
    </svg>
    {children}
  </Box>
);

const CheckoutForm = ({
  client,
  session: { card },
  addons,
  values,
  hpiContent,
  toastManager,
  onSubmit = () => {},
}) => {
  // console.log({ values });
  // console.log({ hpiContent });
  const [error, setError] = useState(null);
  const [addNewCard, setAddNewCard] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [cardComplete, setCardComplete] = useState(false);
  const stripe = useStripe();
  const elements = useElements();

  const handleSubmit = async event => {
    setProcessing(true);
    event.preventDefault();

    if (card && card.pm && !addNewCard) {
      await client.mutate({
        mutation: CREATE_PAYMENT_INTENT,
        variables: {
          addons,
          pm: card.pm,
          values: JSON.stringify(values),
          hpiContent,
        },
        refetchQueries: [
          { query: CURRENT_USER },
          { query: GET_ASYNC_TEMPORARY_RESPONSES },
          { query: UPCOMING_APPOINTMENTS },
          { query: APPOINTMENTS },
          { query: GET_MEMBERSHIPS },
        ],
        awaitRefetchQueries: true,
      });

      setProcessing(false);
      onSubmit();
      return;
    }

    if (!stripe || !elements) {
      // Stripe.js has not loaded yet. Make sure to disable
      // form submission until Stripe.js has loaded.
      setProcessing(false);
      return;
    }

    const cardElement = elements.getElement(CardElement);

    const src = await stripe.createSource(cardElement, { type: 'card' });

    const { error, paymentMethod } = await stripe.createPaymentMethod({
      type: 'card',
      card: cardElement,
    });

    if (error) {
      toastManager.add(
        `${
          error && error.message
            ? `${error.message}`
            : 'An error occured with the payment method. '
        }`,
        { appearance: 'error' }
      );
      console.log('[error]', error);
    } else {
      await client.mutate({
        mutation: CREATE_PAYMENT_INTENT,
        variables: {
          addons,
          pm: paymentMethod.id,
          sourceId: src.source.id,
          values: JSON.stringify(values),
          hpiContent,
        },
        refetchQueries: [
          { query: CURRENT_USER },
          { query: GET_ASYNC_TEMPORARY_RESPONSES },
          { query: UPCOMING_APPOINTMENTS },
          { query: APPOINTMENTS },
        ],
        awaitRefetchQueries: true,
      });
      setProcessing(false);
      onSubmit();
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit}>
        <Box ml="-12px" mr="-12px" mb={4}>
          <Flex flexDirection="column" bg="white" p="12px">
            <Flex flexDirection="row" pb={3}>
              <Text fontSize="16px" fontWeight="600">
                Payment Method
              </Text>

              {card &&
                card.last4 &&
                !addNewCard && (
                  <Text
                    fontSize="16px"
                    fontWeight="600"
                    style={{
                      textDecoration: 'underline',
                      cursor: 'pointer',
                      color: 'rgba(246, 162, 147, 1)',
                    }}
                    onClick={() => setAddNewCard(true)}
                  >
                    + Add Card
                  </Text>
                )}
              {card &&
                card.last4 &&
                addNewCard && (
                  <Text
                    fontSize="16px"
                    fontWeight="600"
                    style={{
                      textDecoration: 'underline',
                      cursor: 'pointer',
                      color: 'rgba(246, 162, 147, 1)',
                    }}
                    onClick={() => setAddNewCard(false)}
                  >
                    Cancel
                  </Text>
                )}
            </Flex>
            <Box
              style={{
                border: '1px solid rgba(0,0,0,0.1)',
                padding: '10px 12px',
                borderRadius: '5px',
              }}
            >
              {card && card.last4 && card.pm && !addNewCard ? (
                <UseCurrentCard />
              ) : (
                <CardElement
                  options={CARD_OPTIONS}
                  onChange={e => {
                    // console.log('complete', e.complete);
                    setError(e.error);
                    setCardComplete(e.complete);
                  }}
                />
              )}
            </Box>
            {error && <ErrorMessage>{error.message}</ErrorMessage>}
          </Flex>
        </Box>
        {/* <Text fontSize="12px" fontWeight="300" style={{ lineHeight: '18px' }}>
        By clicking “Confirm” above, you understand and agree to the agreement
        for Self-Payment of Services and shall pay these charges in full as a
        self-pay patient electing not to use an insurance policy benefit. Visits
        are conducted by doctors or nurse practitioners
      </Text> */}
        <Button
          variant="primary"
          type="submit"
          disabled={
            !stripe ||
            ((!card || !card.last4 || !card.pm || addNewCard) &&
              !cardComplete) ||
            processing
          }
          mt={3}
          flex={1}
          mb={3}
          style={{ width: '100%' }}
        >
          Confirm and Pay
        </Button>
        <Text>
          By Clicking "Confirm and Pay" above, you understand and agree to the{' '}
          <a
            href="https://www.palomahealth.com/legal-doc/agreement-for-self-payment-of-services"
            target="_blank"
          >
            Agreement for Self-Payment of Services
          </a>{' '}
          and shall pay these charges in full as a self-pay patient electing not
          to use an insurance policy benefit. Visits are conducted by doctors or
          nurse practitioners. You also agree to the{' '}
          <a
            href="https://www.palomahealth.com/legal-doc/paloma-health-terms-of-use"
            target="_blank"
          >
            Paloma Health Terms of Use
          </a>{' '}
          and our{' '}
          <a
            href="https://www.palomahealth.com/legal-doc/hipaa-privacy-policy"
            target="_blank"
          >
            HIPAA Privacy Policy
          </a>
          .
        </Text>

        <Flex flexDirection="row" justifyContent="center" alignItems="center">
          <svg
            width="23"
            height="24"
            viewBox="0 0 23 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M17.7931 24H5.20542C5.07722 24.0001 4.95026 23.9753 4.8318 23.9271C4.71333 23.8788 4.6057 23.8081 4.51505 23.7188C4.4244 23.6296 4.35251 23.5237 4.30349 23.407C4.25448 23.2904 4.2293 23.1655 4.2294 23.0393V13.8716C4.2294 13.3406 4.66598 12.9071 5.20542 12.9071H6.38792V11.4266C6.38792 8.65323 8.68129 6.3958 11.4989 6.3958C14.3164 6.3958 16.6098 8.65323 16.6098 11.4266V12.9071H17.7923C18.3317 12.9071 18.7683 13.3406 18.7683 13.8716V23.0393C18.7684 23.1655 18.7432 23.2904 18.6942 23.407C18.6452 23.5237 18.5733 23.6296 18.4827 23.7188C18.392 23.8081 18.2844 23.8788 18.1659 23.9271C18.0475 23.9753 17.9213 24.0001 17.7931 24ZM12.1389 18.9302C13.2977 18.2859 12.833 16.5437 11.4996 16.5437C10.1701 16.5475 9.70607 18.2822 10.8604 18.9302V20.1721C10.8604 21.0083 12.1381 21.0083 12.1381 20.1721L12.1389 18.9302ZM9.3213 12.9063H13.6825V11.4259C13.6825 10.2424 12.7027 9.28171 11.4996 9.28171C10.2966 9.28171 9.3213 10.2424 9.3213 11.4259V12.9063ZM3.92845 11.5436H0.892967C0.396959 11.5436 0 11.1492 0 10.6647C0 10.1802 0.396959 9.7857 0.892967 9.7857H3.92845C4.42064 9.7857 4.82141 10.1802 4.82141 10.6647C4.82141 11.1492 4.42064 11.5436 3.92845 11.5436ZM6.06715 6.84054C5.86064 6.84092 5.66075 6.76886 5.50333 6.63729L3.09872 4.69185C3.0083 4.61866 2.93347 4.5286 2.87853 4.42685C2.82359 4.3251 2.78962 4.21366 2.77857 4.09895C2.76752 3.98423 2.77962 3.86849 2.81417 3.75839C2.84871 3.64829 2.90502 3.546 2.97986 3.45739C3.05394 3.36812 3.14523 3.29414 3.24847 3.23972C3.3517 3.1853 3.46485 3.1515 3.5814 3.14028C3.69796 3.12906 3.81561 3.14063 3.92761 3.17433C4.0396 3.20803 4.14372 3.26319 4.23397 3.33665L6.63859 5.28208C7.28545 5.80557 6.90449 6.84054 6.06715 6.83679V6.84054ZM11.4996 4.76985C11.2629 4.76945 11.036 4.67672 10.8686 4.51197C10.7013 4.34721 10.6071 4.12387 10.6067 3.89088V0.878973C10.6067 0.394488 11.0074 0 11.4996 0C11.9918 0 12.3926 0.394488 12.3926 0.878973V3.89088C12.3926 4.37536 11.9918 4.76985 11.4996 4.76985ZM16.9321 6.84054C16.0909 6.84054 15.7138 5.80557 16.3607 5.28208L18.7653 3.33665C19.6857 2.59417 20.8171 3.95013 19.9005 4.69635L17.4959 6.64179C17.3373 6.77088 17.1379 6.84068 16.9321 6.84054ZM22.107 11.5436H19.0434C18.5474 11.5436 18.1504 11.1492 18.1504 10.6647C18.1504 10.1802 18.5474 9.7857 19.0434 9.7857H22.107C22.603 9.7857 23 10.1802 23 10.6647C23 11.1492 22.603 11.5436 22.107 11.5436Z"
              fill="#F6A293"
            />
          </svg>

          <Text fontSize="12px" fontWeight="300" ml={3}>
            Payment information protected and encrypted
          </Text>
        </Flex>
      </form>
      {!!processing && (
        <PleaseWaitRotatingText text="Thank you! Hold tight as we are saving your information" />
      )}
    </>
  );
};

export default withApollo(withSession(withToastManager(CheckoutForm)));
